import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, Row, Col, Statistic, Segmented, Table, Button, Space, Typography, Empty, Progress } from 'antd';
import {
  ReloadOutlined,
  ThunderboltOutlined,
  DatabaseOutlined,
  TeamOutlined,
  HddOutlined,
  CloudServerOutlined,
} from '@ant-design/icons';
import { api, StorageStats } from '../api';
import { t } from '../i18n';
import { useThemeMode } from '../theme';

type RangeDays = 7 | 30 | 90;

/** One day (UTC midnight) in seconds. */
const DAY = 86400;

/** Admin dashboard: KPI tiles + inline-SVG flashes-per-day chart + top clients
 * (enriched with each client's available/used quota). All data comes from the
 * pre-existing stats endpoints; no new backend. Visible to every admin. */
export function Dashboard() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const [range, setRange] = useState<RangeDays>(30);

  const summary = useQuery({
    queryKey: ['stats-summary'],
    queryFn: api.statsSummary,
    refetchInterval: 30_000,
  });

  const now = Math.floor(Date.now() / 1000);
  const from = Math.floor((now - range * DAY) / DAY) * DAY;
  const to = now;

  const flashes = useQuery({
    queryKey: ['stats-flashes', range],
    queryFn: () => api.flashStats({ from, to, bucket: 'day' }),
    refetchInterval: 30_000,
  });

  // Top-clients counts must NOT follow the chart's range selector (that read as
  // data loss). Use an all-time window, matching the all-time "Flashes total" KPI.
  const flashesAll = useQuery({
    queryKey: ['stats-flashes-all'],
    queryFn: () => api.flashStats({ from: 0, to, bucket: 'day' }),
    refetchInterval: 30_000,
  });

  const storage = useQuery({
    queryKey: ['stats-storage'],
    queryFn: api.storageStats,
    refetchInterval: 30_000,
  });

  // All quota grants, aggregated per client → available/used, so the top-clients
  // table can show each client's quota alongside their flash count.
  const quotas = useQuery({ queryKey: ['all-quotas'], queryFn: () => api.listQuotas() });
  // Every client — so the top-clients table lists them ALL, including clients
  // with zero flashes (the flash stats only include clients who have flashed).
  const users = useQuery({ queryKey: ['users'], queryFn: api.listUsers });

  const flashByClient = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of flashesAll.data?.byClient ?? []) map.set(c.clientUserId, c.count);
    return map;
  }, [flashesAll.data]);

  const uniqueByClient = useMemo(() => {
    const map = new Map<string, number>();
    for (const c of flashesAll.data?.byClient ?? []) map.set(c.clientUserId, c.uniqueDisks);
    return map;
  }, [flashesAll.data]);

  // Every device — so the top-devices table lists them ALL, including devices
  // with zero flashes; deleted devices (in the flash stats but no longer a
  // device row) are shown by a fragment of their id.
  const devices = useQuery({ queryKey: ['devices'], queryFn: api.listDevices });
  const flashByDevice = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of flashesAll.data?.byDevice ?? []) if (d.deviceId) map.set(d.deviceId, d.count);
    return map;
  }, [flashesAll.data]);
  const uniqueByDevice = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of flashesAll.data?.byDevice ?? []) if (d.deviceId) map.set(d.deviceId, d.uniqueDisks);
    return map;
  }, [flashesAll.data]);
  const deviceRows = useMemo(() => {
    const live = (devices.data ?? []).map((d) => ({
      deviceId: d.id,
      label: d.label as string | null,
      deleted: false,
      count: flashByDevice.get(d.id) ?? 0,
      uniqueDisks: uniqueByDevice.get(d.id) ?? 0,
    }));
    const liveIds = new Set((devices.data ?? []).map((d) => d.id));
    // Deleted devices that still have flash history — keep them, shown by id.
    const gone = (flashesAll.data?.byDevice ?? [])
      .filter((d) => d.deviceId && !liveIds.has(d.deviceId))
      .map((d) => ({
        deviceId: d.deviceId as string,
        label: null as string | null,
        deleted: true,
        count: d.count,
        uniqueDisks: d.uniqueDisks,
      }));
    return [...live, ...gone];
  }, [devices.data, flashesAll.data, flashByDevice, uniqueByDevice]);

  const quotaByClient = useMemo(() => {
    const map = new Map<string, { available: number; used: number }>();
    for (const q of quotas.data ?? []) {
      const cur = map.get(q.clientUserId) ?? { available: 0, used: 0 };
      cur.used += q.usedCount;
      const notExpired = q.expiresAt === null || q.expiresAt > now;
      if (notExpired) cur.available += q.totalCount - q.usedCount;
      map.set(q.clientUserId, cur);
    }
    return map;
  }, [quotas.data, now]);

  const refetchAll = () => {
    summary.refetch();
    flashes.refetch();
    flashesAll.refetch();
    quotas.refetch();
    storage.refetch();
    users.refetch();
  };

  const s = summary.data;

  const kpi = (label: string, value: number | string | undefined, accent?: string, onClick?: () => void) => (
    <Col xs={12} sm={8} lg={6} flex="auto">
      <Card
        size="small"
        styles={{ body: { padding: '14px 16px' } }}
        hoverable={!!onClick}
        onClick={onClick}
        style={onClick ? { cursor: 'pointer' } : undefined}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
      >
        <Statistic
          title={
            onClick ? (
              <Space size={4}>
                {label}
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  ›
                </Typography.Text>
              </Space>
            ) : (
              label
            )
          }
          value={value ?? 0}
          valueStyle={{ color: accent, fontVariantNumeric: 'tabular-nums' }}
        />
      </Card>
    </Col>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          <ThunderboltOutlined /> {t('navDashboard')}
        </Typography.Title>
        <div style={{ flex: 1 }} />
        <Button
          icon={<ReloadOutlined />}
          onClick={refetchAll}
          loading={summary.isFetching || flashes.isFetching || storage.isFetching}
        >
          {t('dashRefresh')}
        </Button>
      </div>

      {/* KPI row — Flashes tiles open the detailed history page */}
      <Row gutter={[12, 12]}>
        {kpi(t('dashKpiFlashesToday'), s?.flashesToday, '#5b8def', () => navigate('/flash-history'))}
        {kpi(t('dashKpiFlashesTotal'), s?.flashesTotal, undefined, () => navigate('/flash-history'))}
        {kpi(t('dashKpiFlashesUnique'), s?.flashesUniqueDisks, '#34d399', () => navigate('/flash-history'))}
        {kpi(
          t('dashKpiDevicesOnline'),
          s ? `${s.devicesOnline}/${s.activeDevices}` : undefined,
          s && s.activeDevices > 0 && s.devicesOnline === s.activeDevices ? '#34d399' : undefined,
          () => navigate('/devices'),
        )}
        {kpi(t('dashKpiActiveClients'), s?.activeClients)}
      </Row>

      {/* Storage & disk population */}
      <StoragePanel data={storage.data} loading={storage.isLoading} />

      {/* Flashes-per-day chart */}
      <Card
        size="small"
        title={
          <Space>
            <DatabaseOutlined />
            {t('dashChartTitle')}
          </Space>
        }
        extra={
          <Segmented
            size="small"
            value={range}
            onChange={(v) => setRange(v as RangeDays)}
            options={[
              { label: t('dashRange7'), value: 7 },
              { label: t('dashRange30'), value: 30 },
              { label: t('dashRange90'), value: 90 },
            ]}
          />
        }
      >
        <FlashesChart from={from} to={to} series={flashes.data?.series ?? []} mode={mode} />
      </Card>

      {/* Top clients + per-client quota */}
      <Card size="small" title={<Space><TeamOutlined />{t('dashTopClients')}</Space>}>
        <Table
          size="small"
          rowKey="clientUserId"
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={flashesAll.isLoading || users.isLoading}
          locale={{ emptyText: <Empty description={t('dashChartEmpty')} /> }}
          onRow={(r) => {
            const go = () => navigate(`/flash-history?who=${encodeURIComponent(r.clientUsername ?? r.clientUserId)}`);
            return {
              onClick: go,
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  go();
                }
              },
              role: 'button',
              tabIndex: 0,
              style: { cursor: 'pointer' },
            };
          }}
          dataSource={(users.data ?? [])
            // Every account that can flash — all clients AND all admins/superadmins
            // — even with 0 flashes, so the roster is complete (admin flashes are
            // no-charge but still counted).
            .filter((u) => u.role === 'client' || u.role === 'admin' || u.role === 'superadmin')
            .map((u) => ({
              clientUserId: u.id,
              clientUsername: u.username,
              role: u.role,
              count: flashByClient.get(u.id) ?? 0,
              uniqueDisks: uniqueByClient.get(u.id) ?? 0,
              available: quotaByClient.get(u.id)?.available ?? 0,
              used: quotaByClient.get(u.id)?.used ?? 0,
            }))}
          columns={[
            {
              title: t('dashColClient'),
              dataIndex: 'clientUsername',
              render: (v: string | null, r) => (
                <Space size={6}>
                  <span>{v ?? r.clientUserId.slice(0, 8)}</span>
                  {r.role !== 'client' && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      ({r.role})
                    </Typography.Text>
                  )}
                </Space>
              ),
            },
            {
              title: t('dashColFlashes'),
              dataIndex: 'count',
              align: 'right' as const,
              sorter: (a, b) => a.count - b.count,
              defaultSortOrder: 'descend' as const,
            },
            {
              title: t('dashColUniqueDisks'),
              dataIndex: 'uniqueDisks',
              align: 'right' as const,
              sorter: (a, b) => a.uniqueDisks - b.uniqueDisks,
            },
            { title: t('dashColAvailable'), dataIndex: 'available', align: 'right' as const },
            { title: t('dashColUsed'), dataIndex: 'used', align: 'right' as const },
          ]}
          summary={(rows) => {
            const sum = (pick: (r: (typeof rows)[number]) => number): number =>
              rows.reduce((a, r) => a + pick(r), 0);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Typography.Text strong>{t('dashTotal')}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Typography.Text strong>{sum((r) => r.count)}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Typography.Text strong>{sum((r) => r.uniqueDisks)}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <Typography.Text strong>{sum((r) => r.available)}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">
                  <Typography.Text strong>{sum((r) => r.used)}</Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>

      {/* Top devices by flashes — click a row to open the flash history filtered
          to that device. Uses the device_label snapshot, so deleted/renamed
          controllers still show their captured name. */}
      <Card size="small" title={<Space><HddOutlined />{t('dashTopDevices')}</Space>}>
        <Table
          size="small"
          rowKey="deviceId"
          pagination={false}
          scroll={{ x: 'max-content' }}
          loading={flashesAll.isLoading || devices.isLoading}
          locale={{ emptyText: <Empty description={t('dashChartEmpty')} /> }}
          onRow={(r) => {
            const go = () => r.deviceId && navigate(`/flash-history?device=${encodeURIComponent(r.deviceId)}`);
            return {
              onClick: go,
              onKeyDown: (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  go();
                }
              },
              role: 'button',
              tabIndex: 0,
              style: { cursor: 'pointer' },
            };
          }}
          dataSource={deviceRows}
          columns={[
            {
              title: t('dashColDevice'),
              dataIndex: 'label',
              render: (v: string | null, r) =>
                r.deleted ? (
                  <Typography.Text type="secondary" code>
                    {r.deviceId.slice(0, 8)}… ({t('dashDeviceDeleted')})
                  </Typography.Text>
                ) : (
                  v ?? <Typography.Text code>{r.deviceId.slice(0, 8)}</Typography.Text>
                ),
            },
            {
              title: t('dashColFlashes'),
              dataIndex: 'count',
              align: 'right' as const,
              sorter: (a, b) => a.count - b.count,
              defaultSortOrder: 'descend' as const,
            },
            {
              title: t('dashColUniqueDisks'),
              dataIndex: 'uniqueDisks',
              align: 'right' as const,
              sorter: (a, b) => a.uniqueDisks - b.uniqueDisks,
            },
          ]}
          summary={(rows) => {
            const sum = (pick: (r: (typeof rows)[number]) => number): number =>
              rows.reduce((a, r) => a + pick(r), 0);
            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0}>
                  <Typography.Text strong>{t('dashTotal')}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <Typography.Text strong>{sum((r) => r.count)}</Typography.Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <Typography.Text strong>{sum((r) => r.uniqueDisks)}</Typography.Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
    </Space>
  );
}

/** Small dependency-free donut chart (inline SVG, theme-aware). */
function Donut({
  segments,
  total,
  centerLabel,
  size = 132,
}: {
  segments: Array<{ label: string; value: number; color: string }>;
  total: number;
  centerLabel: string;
  size?: number;
}) {
  const stroke = 16;
  const r = size / 2 - stroke / 2 - 2;
  const c = 2 * Math.PI * r;
  const sum = segments.reduce((s, x) => s + x.value, 0);
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth={stroke} />
      <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
        {sum > 0 &&
          segments
            .filter((sg) => sg.value > 0)
            .map((sg, i) => {
              const dash = (sg.value / sum) * c;
              const el = (
                <circle
                  key={i}
                  cx={size / 2}
                  cy={size / 2}
                  r={r}
                  fill="none"
                  stroke={sg.color}
                  strokeWidth={stroke}
                  strokeDasharray={`${dash} ${c - dash}`}
                  strokeDashoffset={-offset}
                  strokeLinecap="butt"
                />
              );
              offset += dash;
              return el;
            })}
      </g>
      <text x="50%" y="47%" textAnchor="middle" fontSize={24} fontWeight={600} fill="currentColor">
        {total}
      </text>
      <text x="50%" y="62%" textAnchor="middle" fontSize={11} fill="currentColor" opacity={0.55}>
        {centerLabel}
      </text>
    </svg>
  );
}

/** Human-readable byte size (binary units). */
function formatBytes(n: number): string {
  if (!n || n < 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  const v = n / Math.pow(1024, i);
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

/** Storage footprint (backups, DB, server free space) + a live count of
 * installed disks by type, from each active device's latest inventory. */
function StoragePanel({
  data,
  loading,
}: {
  data?: StorageStats;
  loading: boolean;
}) {
  const d = data?.disksByType;
  const usedPct =
    data && data.diskTotal > 0
      ? Math.round(((data.diskTotal - data.diskFree) / data.diskTotal) * 100)
      : 0;

  const tile = (label: string, value: React.ReactNode, accent?: string) => (
    <Col xs={12} sm={8} lg={6} xl={4} flex="auto">
      <Card size="small" styles={{ body: { padding: '14px 16px' } }}>
        <Statistic title={label} value={value as string} valueStyle={{ color: accent }} />
      </Card>
    </Col>
  );

  const segs = [
    { label: t('dashDiskNvme'), value: d?.nvme ?? 0, color: '#5b8def' },
    { label: t('dashDiskSas25'), value: d?.sas25 ?? 0, color: '#34d399' },
    { label: t('dashDiskSas35'), value: d?.sas35 ?? 0, color: '#f59e0b' },
    { label: t('dashDiskUnknown'), value: d?.unknown ?? 0, color: '#9ca3af' },
  ].filter((s) => s.value > 0);
  const diskTotal = d?.total ?? 0;

  return (
    <Card size="small" title={<Space><HddOutlined />{t('dashStorageTitle')}</Space>} loading={loading}>
      {/* Donut sits inline with the storage-footprint tiles to save vertical space */}
      <Row gutter={[12, 12]} align="stretch">
        <Col xs={24} lg={10} xl={8} flex="auto">
          <Card size="small" styles={{ body: { padding: '12px 16px' } }} style={{ height: '100%' }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('dashDisksByType')}
            </Typography.Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 6 }}>
              <Donut
                size={104}
                segments={segs.length ? segs : [{ label: '', value: 1, color: 'var(--border)' }]}
                total={diskTotal}
                centerLabel={t('dashDisksTotal')}
              />
              <Space direction="vertical" size={4} style={{ flex: 1, minWidth: 120 }}>
                {segs.length === 0 && <Typography.Text type="secondary">{t('emptyList')}</Typography.Text>}
                {segs.map((sg) => (
                  <Space key={sg.label} size={8} style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Space size={6}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: sg.color, display: 'inline-block' }} />
                      <Typography.Text style={{ fontSize: 13 }}>{sg.label}</Typography.Text>
                    </Space>
                    <Typography.Text strong style={{ fontSize: 13 }}>
                      {sg.value}
                      <Typography.Text type="secondary" style={{ fontSize: 11, marginLeft: 5 }}>
                        {diskTotal > 0 ? `${Math.round((sg.value / diskTotal) * 100)}%` : ''}
                      </Typography.Text>
                    </Typography.Text>
                  </Space>
                ))}
              </Space>
            </div>
          </Card>
        </Col>
        {tile(t('dashBackupsCount'), data?.backupsCount ?? 0)}
        {tile(t('dashBackupsSize'), formatBytes(data?.backupsBytes ?? 0))}
        {tile(t('dashDbSize'), formatBytes(data?.dbBytes ?? 0))}
        <Col xs={24} sm={16} lg={12} xl={8} flex="auto">
          <Card size="small" styles={{ body: { padding: '14px 16px' } }} style={{ height: '100%' }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                <Space size={6}>
                  <CloudServerOutlined />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {t('dashServerFree')}
                  </Typography.Text>
                </Space>
                <Typography.Text strong>{formatBytes(data?.diskFree ?? 0)}</Typography.Text>
              </Space>
              <Progress
                percent={usedPct}
                size="small"
                status={usedPct >= 90 ? 'exception' : 'normal'}
                strokeColor={usedPct >= 90 ? undefined : '#5b8def'}
              />
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('dashServerUsedOf', {
                  used: formatBytes((data?.diskTotal ?? 0) - (data?.diskFree ?? 0)),
                  total: formatBytes(data?.diskTotal ?? 0),
                })}
              </Typography.Text>
            </Space>
          </Card>
        </Col>
      </Row>
    </Card>
  );
}

/** Inline-SVG bar chart, one bar per day in [from,to]. Zero-days are rendered as
 * empty slots so the time axis is continuous. Native <title> tooltips per bar. */
function FlashesChart({
  from,
  to,
  series,
  mode,
}: {
  from: number;
  to: number;
  series: Array<{ ts: number; count: number }>;
  mode: 'light' | 'dark';
}) {
  // Local midnights, matching the tz-aligned buckets the server returns. Going
  // through Date (rather than flooring to a multiple of 86400, which is UTC
  // midnight) also keeps the grid right across a DST change.
  const dayStart = (unix: number): number => {
    const d = new Date(unix * 1000);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  };
  const nextDay = (unix: number): number => {
    const d = new Date(unix * 1000);
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 1);
    return Math.floor(d.getTime() / 1000);
  };
  const byTs = new Map(series.map((p) => [dayStart(p.ts), p.count]));

  const bars: Array<{ ts: number; count: number }> = [];
  for (let d = dayStart(from), end = dayStart(to); d <= end; d = nextDay(d)) {
    bars.push({ ts: d, count: byTs.get(d) ?? 0 });
  }

  const total = bars.reduce((n, b) => n + b.count, 0);
  if (total === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashChartEmpty')} />;
  }

  const W = 1000;
  const H = 220;
  const padTop = 12;
  const padBottom = 22;
  const chartH = H - padTop - padBottom;
  const maxCount = Math.max(1, ...bars.map((b) => b.count));
  const slot = W / bars.length;
  const barW = Math.max(1, slot * 0.7);

  const axis = mode === 'dark' ? '#3a4157' : '#d9d9d9';
  const label = mode === 'dark' ? '#8b95ad' : '#6b7488';
  // Local, matching the tz-aligned buckets the server now returns.
  const fmt = (ts: number) => {
    const dt = new Date(ts * 1000);
    return `${String(dt.getDate()).padStart(2, '0')}.${String(dt.getMonth() + 1).padStart(2, '0')}`;
  };
  // Show at most ~8 x-axis labels to avoid crowding.
  const labelEvery = Math.max(1, Math.ceil(bars.length / 8));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" height={H} role="img" aria-label={t('dashChartTitle')}>
      <defs>
        <linearGradient id="barfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7c5cff" />
          <stop offset="100%" stopColor="#5b8def" />
        </linearGradient>
      </defs>
      {/* baseline */}
      <line x1={0} y1={padTop + chartH} x2={W} y2={padTop + chartH} stroke={axis} strokeWidth={1} />
      {/* max gridline + label */}
      <line x1={0} y1={padTop} x2={W} y2={padTop} stroke={axis} strokeWidth={1} strokeDasharray="3 4" opacity={0.6} />
      <text x={4} y={padTop - 2} fill={label} fontSize={11}>
        {maxCount}
      </text>
      {bars.map((b, i) => {
        const h = (b.count / maxCount) * chartH;
        const x = i * slot + (slot - barW) / 2;
        const y = padTop + chartH - h;
        return (
          <g key={b.ts}>
            {b.count > 0 && (
              <rect x={x} y={y} width={barW} height={h} rx={Math.min(2, barW / 2)} fill="url(#barfill)">
                <title>{t('dashChartFlashesOn', { count: b.count, date: fmt(b.ts) })}</title>
              </rect>
            )}
            {i % labelEvery === 0 && (
              <text x={i * slot + slot / 2} y={H - 6} fill={label} fontSize={10} textAnchor="middle">
                {fmt(b.ts)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
